import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { TradingBot } from './TradingBotsManager';

interface CreateBotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateBot: (botData: Partial<TradingBot>) => void;
}

export function CreateBotModal({ isOpen, onClose, onCreateBot }: CreateBotModalProps) {
  const [name, setName] = useState('');
  const [strategy, setStrategy] = useState('scalping');
  const [pair, setPair] = useState('BTC/USDT');
  const [investment, setInvestment] = useState(1000);
  const [buyThreshold, setBuyThreshold] = useState(1.5);
  const [sellThreshold, setSellThreshold] = useState(1.5);
  const [stopLoss, setStopLoss] = useState(5);
  const [takeProfit, setTakeProfit] = useState(10);
  const [maxTradesPerDay, setMaxTradesPerDay] = useState(20);
  const [tradeAmount, setTradeAmount] = useState(100);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    onCreateBot({
      name,
      strategy: strategy as any,
      pair,
      investment,
      config: {
        buyThreshold,
        sellThreshold,
        stopLoss,
        takeProfit,
        maxTradesPerDay,
        tradeAmount,
      }
    });

    // Reset form
    setName('');
    setStrategy('scalping');
    setPair('BTC/USDT');
    setInvestment(1000);
    setBuyThreshold(1.5);
    setSellThreshold(1.5);
    setStopLoss(5);
    setTakeProfit(10);
    setMaxTradesPerDay(20);
    setTradeAmount(100);
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-violet-500/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Crear Nuevo Bot de Trading</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Básica */}
          <div className="space-y-4">
            <h3 className="text-lg text-violet-400">Información Básica</h3>
            
            <div>
              <Label htmlFor="name">Nombre del Bot</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Bot Scalping BTC"
                required
                className="bg-black/40 border-violet-500/30"
              />
            </div>

            <div>
              <Label htmlFor="strategy">Estrategia</Label>
              <Select value={strategy} onValueChange={setStrategy}>
                <SelectTrigger className="bg-black/40 border-violet-500/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-violet-500/30">
                  <SelectItem value="scalping">Scalping - Operaciones rápidas</SelectItem>
                  <SelectItem value="swing">Swing - Mediano plazo</SelectItem>
                  <SelectItem value="trend">Tendencia - Sigue la tendencia</SelectItem>
                  <SelectItem value="grid">Grid - Trading en cuadrícula</SelectItem>
                  <SelectItem value="arbitrage">Arbitraje - Diferencias de precio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="pair">Par de Trading</Label>
              <Select value={pair} onValueChange={setPair}>
                <SelectTrigger className="bg-black/40 border-violet-500/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-violet-500/30">
                  <SelectItem value="BTC/USDT">BTC/USDT</SelectItem>
                  <SelectItem value="ETH/USDT">ETH/USDT</SelectItem>
                  <SelectItem value="BNB/USDT">BNB/USDT</SelectItem>
                  <SelectItem value="SOL/USDT">SOL/USDT</SelectItem>
                  <SelectItem value="ADA/USDT">ADA/USDT</SelectItem>
                  <SelectItem value="MATIC/USDT">MATIC/USDT</SelectItem>
                  <SelectItem value="DOT/USDT">DOT/USDT</SelectItem>
                  <SelectItem value="AVAX/USDT">AVAX/USDT</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="investment">Inversión Inicial ($)</Label>
              <Input
                id="investment"
                type="number"
                value={investment}
                onChange={(e) => setInvestment(Number(e.target.value))}
                min="100"
                step="100"
                required
                className="bg-black/40 border-violet-500/30"
              />
            </div>
          </div>

          {/* Configuración de Trading */}
          <div className="space-y-4">
            <h3 className="text-lg text-violet-400">Configuración de Trading</h3>
            
            <div>
              <Label htmlFor="buyThreshold">
                Umbral de Compra: {buyThreshold}%
                <span className="text-xs text-gray-400 ml-2">
                  (Compra cuando el precio baje este %)
                </span>
              </Label>
              <Slider
                id="buyThreshold"
                value={[buyThreshold]}
                onValueChange={(values) => setBuyThreshold(values[0])}
                min={0.5}
                max={10}
                step={0.5}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="sellThreshold">
                Umbral de Venta: {sellThreshold}%
                <span className="text-xs text-gray-400 ml-2">
                  (Vende cuando el precio suba este %)
                </span>
              </Label>
              <Slider
                id="sellThreshold"
                value={[sellThreshold]}
                onValueChange={(values) => setSellThreshold(values[0])}
                min={0.5}
                max={10}
                step={0.5}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="stopLoss">
                Stop Loss: {stopLoss}%
                <span className="text-xs text-gray-400 ml-2">
                  (Detener pérdidas en este %)
                </span>
              </Label>
              <Slider
                id="stopLoss"
                value={[stopLoss]}
                onValueChange={(values) => setStopLoss(values[0])}
                min={1}
                max={20}
                step={1}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="takeProfit">
                Take Profit: {takeProfit}%
                <span className="text-xs text-gray-400 ml-2">
                  (Tomar ganancias en este %)
                </span>
              </Label>
              <Slider
                id="takeProfit"
                value={[takeProfit]}
                onValueChange={(values) => setTakeProfit(values[0])}
                min={1}
                max={50}
                step={1}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="tradeAmount">Monto por Operación ($)</Label>
              <Input
                id="tradeAmount"
                type="number"
                value={tradeAmount}
                onChange={(e) => setTradeAmount(Number(e.target.value))}
                min="10"
                step="10"
                required
                className="bg-black/40 border-violet-500/30"
              />
            </div>

            <div>
              <Label htmlFor="maxTrades">Máximo de Operaciones por Día</Label>
              <Input
                id="maxTrades"
                type="number"
                value={maxTradesPerDay}
                onChange={(e) => setMaxTradesPerDay(Number(e.target.value))}
                min="1"
                max="100"
                required
                className="bg-black/40 border-violet-500/30"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-violet-500/30"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            >
              Crear Bot
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
